<template>
  <div class="date-picker-ui">
    <VueDatePicker
      ref="dpRef"
      v-model="model"
      model-type="yyyy-MM-dd"
      format="dd-MM-yyyy"
      :disabled-dates="disabledDates"
      :enable-time-picker="false"
      :range="range"
      :model-auto="allowOpenEnd"
      :ui="{
        menu: 'date-picker-ui__menu',
        calendarCell: 'date-picker-ui__calendar-cell',
      }"
      :position="position"
      :teleport-center="position === 'center'"
      :auto-position="autoPosition"
      :disabled="disabled"
    >
      <template #trigger>
        <slot name="trigger">
          <DatePickerBtn
            :selected-date="model"
            :variant="variant"
            :allow-open-end="allowOpenEnd"
            :placeholder="placeholder"
            :disabled="disabled"
          />
        </slot>
      </template>
      <template #arrow-left>
        <i class="fr fr-chevron-left" aria-hidden="true" />
      </template>
      <template #arrow-right>
        <i class="fr fr-chevron-right" aria-hidden="true" />
      </template>
      <template v-if="$slots['hint']" #top-extra>
        <div class="date-picker-ui__hint">
          <slot name="hint" />
        </div>
      </template>
      <template #action-row="{ internalModelValue, selectDate }">
        <div class="date-picker-ui__action-row">
          <VBtn flat class="date-picker-flat" @click="clear">Clear</VBtn>
          <VBtn
            class="date-picker"
            :class="[internalModelValue && 'v-btn-ui--active']"
            @click="selectDate"
            >Apply</VBtn
          >
        </div>
      </template>
    </VueDatePicker>
  </div>
</template>

<script setup lang="ts">
  // Types
  import type { PropType } from 'vue';
  import type { RangeConfig } from '@vuepic/vue-datepicker';

  // Components
  import VueDatePicker from '@vuepic/vue-datepicker';
  import DatePickerBtn from '~/misc/date-picker/btn/DatePickerBtn.vue';
  import VBtn from '~/misc/v-btn';

  defineProps({
    variant: {
      type: String as PropType<'default' | 'compact'>,
      default: 'default',
    },
    position: {
      type: String as PropType<'left' | 'center' | 'right'>,
      default: 'center',
    },
    autoPosition: {
      type: [Boolean, String] as PropType<boolean | 'top' | 'bottom'>,
      default: false,
    },
    allowOpenEnd: {
      type: Boolean,
      default: false,
    },
    range: {
      type: [Boolean, Object] as PropType<boolean | RangeConfig>,
      default: false,
    },
    disabledDates: {
      type: [Array, Function] as PropType<Date[] | string[] | ((date: Date) => boolean)>,
      default: undefined,
    },
    disabled: {
      type: Boolean,
      default: false,
    },
    placeholder: {
      type: String,
      default: 'Select date(s)',
    },
  });

  const model = defineModel({
    type: [Array, String, null] as PropType<[string, string] | string | null>,
    default: null,
  });

  const dpRef: Ref<typeof VueDatePicker | null> = ref<typeof VueDatePicker | null>(null);

  const clear = () => {
    dpRef.value?.clearValue();
  };
</script>
